'use client';

// Spec 113 — step 1 of the unified 4-step creation flow. Replaces the old
// Generate/Manual tab fork with a name-first entry: a name input plus a
// segmented control to PICK the creation method (FR-003). Advancement is the
// dialog footer's Next button — both methods lead to the same step 2 (blank
// for manual, AI-prefilled for generate; FR-004). "Generate with AI" is the
// left, default-selected segment.
//
// Optionally surfaces a single resumable DRAFT row (the most-recently-edited
// of the two per-flow drafts) so a returning creator can pick up where they
// left off (FR-019/FR-020).
import { c as _c } from "react/compiler-runtime";
import { BorderBeam } from 'border-beam';
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { AiFileIconSolid, ArrowRight01Icon, Clock01Icon, TaskEdit01IconSolid } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { PROPOSAL_FIELDS } from '@/config/proposal-fields';
import { cn } from '@/lib/ui/utils';
export type CreationMethod = 'generate' | 'manual';
export interface ProposalStepNameEntryProps {
  /** Lifted name value (dialog-owned local state until Next is pressed). */
  name: string;
  onNameChange: (value: string) => void;
  /** Selected creation method (dialog-owned). "generate" is the default. */
  method: CreationMethod;
  onMethodChange: (method: CreationMethod) => void;
  /** Commit the entered name + selected method and advance (Enter-key seam;
   *  the footer Next is the primary affordance). Only fired when the name is
   *  non-empty. */
  onSubmit: () => void;
  /** Most-recent resumable draft, or null when neither flow holds one. */
  recentDraft: {
    title: string;
    flow: CreationMethod;
  } | null;
  /** Resume the surfaced draft (restore store + jump to its step). */
  onResumeDraft: () => void;
}
export function ProposalStepNameEntry(t0) {
  const $ = _c(51);
  const {
    name,
    onNameChange,
    method,
    onMethodChange,
    onSubmit,
    recentDraft,
    onResumeDraft
  } = t0;
  const t = useTranslations("launchpad.form");
  const tNameEntry = useTranslations("launchpad.form.nameEntry");
  let t1;
  if ($[0] !== name) {
    t1 = name.trim();
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasName = t1.length > 0;
  const t2 = method === "generate";
  let t3;
  if ($[2] !== t) {
    t3 = t("titleLabel");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== tNameEntry) {
    t4 = tNameEntry("placeholder");
    $[4] = tNameEntry;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onNameChange) {
    t5 = e => onNameChange(e.target.value);
    $[6] = onNameChange;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== hasName || $[9] !== onSubmit) {
    t6 = e_0 => {
      if (e_0.key !== "Enter" || e_0.nativeEvent.isComposing || !hasName) {
        return;
      }
      e_0.preventDefault();
      onSubmit();
    };
    $[8] = hasName;
    $[9] = onSubmit;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("border-transparent border-0 hover:border-transparent focus:border-transparent focus-visible:border-transparent active:border-transparent focus-visible:ring-inset bg-background-secondary dark:bg-background-tertiary");
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== name || $[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t8 = <InputField aria-label={t3} size="xl" placeholder={t4} value={name} onChange={t5} onKeyDown={t6} maxLength={PROPOSAL_FIELDS.title.maxLength} className={t7} />;
    $[12] = name;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t2 || $[19] !== t8) {
    t9 = <BorderBeam active={t2} colorVariant="colorful" theme="auto">{t8}</BorderBeam>;
    $[18] = t2;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== onMethodChange) {
    t10 = v => onMethodChange(v === "manual" ? "manual" : "generate");
    $[21] = onMethodChange;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== tNameEntry) {
    t11 = tNameEntry("methodLabel");
    $[23] = tNameEntry;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Icon icon={AiFileIconSolid} className="size-3.5" />;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== tNameEntry) {
    t13 = tNameEntry("chooseGenerate");
    $[26] = tNameEntry;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13) {
    t14 = <TabsTrigger value="generate" className="gap-2.5">{t12}{t13}</TabsTrigger>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Icon icon={TaskEdit01IconSolid} className="size-3.5" />;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== tNameEntry) {
    t16 = tNameEntry("chooseManual");
    $[31] = tNameEntry;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16) {
    t17 = <TabsTrigger value="manual" className="gap-2.5">{t15}{t16}</TabsTrigger>;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t11 || $[36] !== t14 || $[37] !== t17) {
    t18 = <TabsList variant="segmented" size="md" aria-label={t11} className="group-data-[orientation=horizontal]/tabs:h-12">{t14}{t17}</TabsList>;
    $[35] = t11;
    $[36] = t14;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== method || $[40] !== t10 || $[41] !== t18) {
    t19 = <Tabs value={method} onValueChange={t10}>{t18}</Tabs>;
    $[39] = method;
    $[40] = t10;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== onResumeDraft || $[44] !== recentDraft || $[45] !== tNameEntry) {
    t20 = recentDraft !== null && <button type="button" onClick={onResumeDraft} className="group flex w-full items-center gap-3 rounded-panel-row border bg-background-secondary px-4 py-3 text-left transition-colors hover:bg-background-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary"><Icon icon={Clock01Icon} className="size-4 shrink-0 text-foreground-secondary" /><span className="flex min-w-0 flex-1 flex-col"><span className="text-caption font-medium uppercase tracking-wide text-foreground-tertiary">{tNameEntry("draftTag")}</span><span className="truncate text-body text-foreground-primary">{recentDraft.title}</span></span><Icon icon={ArrowRight01Icon} className="size-4 shrink-0 text-foreground-tertiary transition-transform group-hover:translate-x-0.5" /></button>;
    $[43] = onResumeDraft;
    $[44] = recentDraft;
    $[45] = tNameEntry;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t19 || $[48] !== t20 || $[49] !== t9) {
    t21 = <div className="flex flex-col gap-4 pt-1">{t9}{t19}{t20}</div>;
    $[47] = t19;
    $[48] = t20;
    $[49] = t9;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  return t21;
}